/**
 * O ENDEREÇO DE UM PDF DO SISTEMA — com o token, em UM lugar só.
 *
 * ═══ O DEFEITO QUE ISTO FECHA (auditoria de 17/08/2026) ════════════════════
 * "Imprimir / PDF" do A Pagar abria **401**. O link era um `<a href>` cru
 * (`/api/pdf/cp/:id`), e a família `/api/pdf/*` não é autenticada por header:
 * o `authMiddleware` aceita o token pela QUERY STRING justamente nesta família
 * (`middleware/auth.ts:222`), porque `window.open`/`<a target=_blank>` não
 * mandam `Authorization`. Seis links do app anexavam `?token=`; três não —
 * o da v2 (`ListaContasCp`) e os DOIS da v1 (`ContasPagar`).
 *
 * ═══ POR QUE UM HELPER, E NÃO "espelhar o padrão" na mão ═══════════════════
 * Espelhar na mão foi exatamente o que produziu o furo: o padrão existia em 6
 * lugares e o 7º nasceu sem ele, calado — um `<a>` não tem como falhar em teste
 * de fetch, e 401 só aparece pra quem CLICA. Com um dono só, o link novo herda
 * o token por construção, e a sonda de `tests/pdf-link-leva-token.test.ts`
 * recusa qualquer `/api/pdf/` escrito fora daqui.
 *
 * ═══ O QUE ISTO NÃO CONSERTAVA (declarado em 17/08; fechado em 21/10 pelo bilhete, ver abaixo) ═══
 * Token em query string VAZA — histórico do navegador e log de proxy ficam com
 * a URL inteira. A rota `/pdf/ficha` já diz isso com estas palavras, e o
 * mecanismo foi aceito ali como o preço de abrir PDF em aba nova. Este arquivo
 * NÃO muda esse trade-off: ele o concentra num lugar, que é o pré-requisito
 * pra um dia trocá-lo (por link assinado de curta duração, ou por fetch com
 * header + blob) sem caçar `<a>` espalhado por sete arquivos.
 */

/**
 * Monta o endereço de um PDF anexando o token da sessão.
 *
 * `caminho` é o path completo da rota (`/api/pdf/cp/abc`), com ou sem query
 * própria — `?competencia=2026-08` continua valendo e o token entra com `&`.
 *
 * Sem token (sessão ainda carregando, ou expirada) o endereço sai LIMPO, sem
 * `?token=` vazio: mandar o parâmetro vazio faria o servidor tratar como
 * "token inválido" em vez de "sem token", e a mensagem que o operador leria
 * seria a errada.
 */
export function hrefPdf(caminho: string, token: string | null | undefined): string {
  const t = token?.trim();
  if (!t) return caminho;
  const sep = caminho.includes("?") ? "&" : "?";
  // 🎟️ 48 §4: com bilhete válido DESTA sessão, a URL leva o bilhete (opaco, 10 min, só /api/pdf/*), nunca o JWT cru.
  const b = bilheteValido(t);
  if (b) return `${caminho}${sep}bilhete=${encodeURIComponent(b)}`;
  return `${caminho}${sep}token=${encodeURIComponent(t)}`;
}

// ═══ O BILHETE (relatório 48 §4, 21/10) ═══════════════════════════════════════════════════════════════════════════
// O JWT de 1 h na URL ficava no histórico e no log de proxy, e valia para a API inteira. A tela agora troca o crachá
// (no header) por um bilhete cifrado de 10 min que só abre a família /api/pdf/* (src/api/lib/bilhetePdf.ts), guardado
// aqui em memória (nunca em localStorage) e renovado pelo AuthProvider a cada RENOVAR_BILHETE_MS. Sem bilhete (ainda
// chegando, lente "ver como", falha de rede), o link cai no `?token=` de antes: nunca quebra.
// O `<a href>` é montado no RENDER; uma tela aberta há mais de 10 min teria um bilhete vencido no link. Por isso
// `trocarNoClique` (instalado uma vez pelo AuthProvider) reescreve o link da família /api/pdf/ no instante do clique
// (clique, clique do meio e menu do botão direito) com o bilhete do momento.

/** Renova antes de vencer: o bilhete vale 10 min no servidor; aqui se troca a cada 4 e se descarta com menos de 45 s. */
export const RENOVAR_BILHETE_MS = 4 * 60_000;
const FOLGA_MS = 45_000;
let atual: { bilhete: string; ateMs: number; paraToken: string } | null = null;

/** O bilhete em memória, só se for da MESMA sessão e ainda tiver folga. */
export function bilheteValido(token: string, agoraMs = Date.now()): string | null {
  if (!atual || atual.paraToken !== token || atual.ateMs - agoraMs < FOLGA_MS) return null;
  return atual.bilhete;
}

/** Guarda o bilhete que o servidor devolveu (exportada para o teste). */
export function guardarBilhetePdf(bilhete: string, venceEm: string, paraToken: string): void {
  const ateMs = Date.parse(venceEm);
  atual = Number.isFinite(ateMs) && bilhete ? { bilhete, ateMs, paraToken } : null;
}

/** Saiu da sessão: o bilhete some junto. */
export function limparBilhetePdf(): void {
  atual = null;
}

/** Pede um bilhete novo com o crachá no header. Falha = fica sem bilhete (o link usa o `?token=`). */
export async function renovarBilhetePdf(token: string | null | undefined, pedir: (token: string) => Promise<{ bilhete: string; vence_em: string }>): Promise<boolean> {
  const t = token?.trim();
  if (!t) { limparBilhetePdf(); return false; }
  try {
    const r = await pedir(t);
    guardarBilhetePdf(r.bilhete, r.vence_em, t);
    return !!atual;
  } catch {
    return false;
  }
}

/** O endereço com o bilhete do MOMENTO no lugar do `?bilhete=`/`?token=` que o render pôs. Fora de /api/pdf/: igual. PURA. */
export function comBilheteDoMomento(href: string, agoraMs = Date.now()): string {
  if (!atual) return href;
  let u: URL;
  try { u = new URL(href, "http://x.invalid"); } catch { return href; }
  if (!u.pathname.startsWith("/api/pdf/") || (!u.searchParams.has("bilhete") && !u.searchParams.has("token"))) return href;
  const b = bilheteValido(atual.paraToken, agoraMs);
  if (!b) return href;
  u.searchParams.delete("token");
  u.searchParams.set("bilhete", b);
  return /^https?:/.test(href) ? u.toString() : `${u.pathname}${u.search}${u.hash}`;
}

let instalado = false;
/** Reescreve, no clique, todo `<a>` da família /api/pdf/ com o bilhete do momento. Idempotente. */
export function trocarNoClique(doc: Document = document): void {
  if (instalado) return;
  instalado = true;
  const trocar = (ev: Event) => {
    const a = (ev.target as Element | null)?.closest?.("a[href]") as HTMLAnchorElement | null;
    if (!a) return;
    const novo = comBilheteDoMomento(a.getAttribute("href") ?? "");
    if (novo !== a.getAttribute("href")) a.setAttribute("href", novo);
  };
  for (const tipo of ["click", "auxclick", "contextmenu"]) doc.addEventListener(tipo, trocar, true);
}
